import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Comment anchors — the two-way link between a comment and the place in the
 * content it is about (2026-08-26, Fredrik).
 *
 * The arrow only ever pointed one way: a marker in the document opened the
 * comment (`openComment`). Reading the list and finding the passage meant
 * hunting for it by eye. This is the other direction, plus the scroll spy
 * that says which comments the reader is currently looking at.
 *
 * WHY A CONTEXT AND NOT A PROP
 *
 * The rail is rendered INSIDE the viewers, so a context reaches it with no
 * threading, and — more to the point — the rail stays format-blind. Every
 * format answers the same two questions:
 *
 *   reveal(id)          put this comment's anchor on screen
 *   visibleCommentIds   which anchors are on screen right now
 *
 * and each viewer answers them in the way its own content allows. Text
 * formats scroll the page to a <mark>; the snapshot scrolls INSIDE its iframe
 * because that is where its coordinate space lives; video seeks the player.
 * Adding a format means adding one adapter, not touching the panel.
 *
 * MARKERS CARRY THEIR IDS IN THE DOM. Every marker renderer stamps
 * `data-comment-id`, and the ones that can cover a CLUSTER of overlapping
 * comments also stamp `data-comment-ids` (a space-separated list, matched
 * with the `~=` attribute selector). A cluster marker is the anchor for every
 * comment in it, so revealing any of them lands on the same element.
 */

export interface CommentAnchorApi {
  /** Put the comment's anchor on screen. False when it has none. */
  reveal: (commentId: string) => boolean;
  /** Anchors currently on screen, in document order. */
  visibleCommentIds: string[];
  /**
   * Mark this comment's anchor as the current one, or clear with null
   * (2026-08-26, Fredrik: "the overlay highlight in the text could be
   * highlighted too so that it is clear which ones belong together").
   *
   * The rail knows which row is current; only the format knows which element
   * that is. So the rail says WHICH and the adapter says HOW, the same split
   * as reveal(). Idempotent, and safe to call with an id that has no anchor.
   */
  setActive: (commentId: string | null) => void;
  /**
   * EVERY anchored comment, in the order its anchor appears in the content
   * (2026-08-26, Fredrik). The rail sorts by this so the list reads in the
   * same order as the document.
   *
   * Comments with no anchor are ABSENT rather than pushed to an index — the
   * rail decides where they go, and it is the only caller that knows which
   * comments it is holding. An adapter that cannot order its anchors returns
   * an empty array, which the rail reads as "leave the list alone".
   */
  orderedCommentIds: string[];
  /** Does this comment have an anchor at all? A comment written without a
      selection (the "Add comment" fallback) has nothing to scroll to. */
  hasAnchor: (commentId: string) => boolean;
  /**
   * Point at a comment's anchor without selecting it (2026-08-27, Fredrik:
   * "some hover effects or something that makes it clear how the comment in
   * the rail links to the comment in the content when there are more
   * comments" — Word's model).
   *
   * Same WHICH/HOW split as setActive: the rail says which comment the
   * pointer is over, the adapter says what to light up. Null clears.
   */
  setHovered: (commentId: string | null) => void;
  /**
   * The OTHER direction of the same link: the pointer is over a marker in
   * the CONTENT, and the rail row for it should light up. The adapter owns
   * this because only it knows where its markers are and how to hear about
   * them; the rail just reads the answer.
   */
  hoveredCommentId: string | null;
  /**
   * Where this comment's anchor is on screen right now, in viewport
   * coordinates — or null if it has none, or the format has no place (a
   * video's anchor is a moment).
   *
   * The rail uses it to line its open row up WITH the passage rather than
   * putting the passage in the middle of the screen and the row at the top
   * of the rail, which is what made the two read as unrelated. Measured
   * rather than assumed: both scrolls clamp at the ends of their scrollers,
   * so a fixed fraction of the viewport agrees with reality in the middle of
   * a document and nowhere else.
   */
  anchorRect: (commentId: string) => DOMRect | null;
}

const NO_ANCHORS: CommentAnchorApi = {
  reveal: () => false,
  setActive: () => {},
  visibleCommentIds: [],
  orderedCommentIds: [],
  hasAnchor: () => false,
  setHovered: () => {},
  hoveredCommentId: null,
  anchorRect: () => null
};

const CommentAnchorContext = createContext<CommentAnchorApi>(NO_ANCHORS);

export const CommentAnchorProvider = CommentAnchorContext.Provider;

/** Falls back to a no-op API, so a panel rendered outside a viewer (tests,
    the dashboard) degrades to "no jump targets" rather than throwing. */
export function useCommentAnchors(): CommentAnchorApi {
  return useContext(CommentAnchorContext);
}

/** Every id a marker element stands for. */
export function idsOnElement(el: Element): string[] {
  const list = (el.getAttribute('data-comment-ids') || '').split(/\s+/).filter(Boolean);
  const primary = el.getAttribute('data-comment-id');
  if (primary && !list.includes(primary)) list.unshift(primary);
  return list;
}

function selectorFor(commentId: string): string {
  // CSS.escape keeps a Mongo id safe here even though they are hex — a
  // selector built by concatenation is the kind of thing that breaks the day
  // an id format changes.
  const safe = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(commentId) : commentId;
  return `[data-comment-id="${safe}"], [data-comment-ids~="${safe}"]`;
}

/**
 * THE READING POSITION IS A BAND, NOT THE SCREEN (2026-08-26, Fredrik:
 * "it now seems like any paragraph gets highlighted when in the reading
 * space, that's not what I wanted").
 *
 * Both adapters answered "which anchors are on screen", and on a web page
 * that meant the whole 900px capture viewport — so every comment anywhere in
 * view took its turn being the reading position as you scrolled, which reads
 * as the rail reacting to nothing in particular. A reader's attention is a
 * line, not a page.
 *
 * Expressed as fractions of the viewport so both adapters agree on where
 * that line is: one of them measures with an IntersectionObserver and the
 * other with arithmetic on stored coordinates, and the whole point is that
 * they behave the same.
 */
export const READING_BAND = { top: 0.22, bottom: 0.55 };

/**
 * The current anchor carries a class, so a marker and its row are visibly
 * the same thing. Exclusive by construction: clearing every other element
 * first means a stale highlight cannot survive a re-render or a format
 * switch, which is the failure this would otherwise have.
 */
export const ACTIVE_ANCHOR_CLASS = 'comment-anchor-active';

export function applyActiveAnchor(root: ParentNode | null, commentId: string | null): void {
  if (!root) return;
  for (const el of Array.from(root.querySelectorAll('.' + ACTIVE_ANCHOR_CLASS))) {
    el.classList.remove(ACTIVE_ANCHOR_CLASS);
  }
  if (!commentId) return;
  const el = root.querySelector(selectorFor(commentId));
  if (el) el.classList.add(ACTIVE_ANCHOR_CLASS);
}

/**
 * The pointed-at anchor. A SEPARATE class from the active one rather than a
 * reuse of it: hovering is a question ("is it this one?") and opening is an
 * answer, so they must be able to show at the same time on two different
 * comments without one erasing the other.
 */
export const HOVER_ANCHOR_CLASS = 'comment-anchor-hover';

export function applyHoverAnchor(root: ParentNode | null, commentId: string | null): void {
  if (!root) return;
  for (const el of Array.from(root.querySelectorAll('.' + HOVER_ANCHOR_CLASS))) {
    el.classList.remove(HOVER_ANCHOR_CLASS);
  }
  if (!commentId) return;
  const el = root.querySelector(selectorFor(commentId));
  if (el) el.classList.add(HOVER_ANCHOR_CLASS);
}

/**
 * Put a list in document order (2026-08-26, Fredrik).
 *
 * The rail and the consultations panel both hold a list the API ordered by
 * time, next to content the reader moves through by position — so scrolling
 * made the highlight jump 1 -> 3 -> 2 down a list that was standing still.
 *
 * ONE COPY, because there are two lists. A sort written twice is exactly how
 * the two viewers drifted apart before this rail existed.
 *
 * Anything the adapter did not place keeps its API position at the END,
 * where it cannot push a placed row out of sequence: a video finding with no
 * timestamp has no place in the timeline, and inventing one is worse than
 * admitting it. An EMPTY `order` means the adapter cannot answer (or the
 * markers have not rendered yet) and returns the list untouched — the caller
 * gets the API order it already had, never a scrambled one.
 */
export function orderByAnchors<T>(items: T[], idOf: (item: T) => string, order: string[]): T[] {
  if (order.length === 0) return items;
  const rank = new Map(order.map((id, i) => [id, i] as const));
  return items
    .map((item, i) => ({ item, i }))
    .sort((a, b) => {
      const ra = rank.get(idOf(a.item));
      const rb = rank.get(idOf(b.item));
      if (ra === undefined && rb === undefined) return a.i - b.i;
      if (ra === undefined) return 1;
      if (rb === undefined) return -1;
      // The index tiebreak is explicit rather than leaning on sort stability.
      return ra - rb || a.i - b.i;
    })
    .map(x => x.item);
}

/**
 * Flash an element without destroying whatever it already had.
 *
 * Same care as openComment's: a marker addressed to the reader already
 * carries an inline outline, and resetting to '' would strip it. This uses a
 * class so the animation is CSS-owned, and the class is removed again.
 */
export function flashAnchor(el: Element): void {
  el.classList.remove('comment-anchor-flash');
  // Force a reflow so re-flashing the same element restarts the animation
  // instead of doing nothing.
  void (el as HTMLElement).offsetWidth;
  el.classList.add('comment-anchor-flash');
  window.setTimeout(() => el.classList.remove('comment-anchor-flash'), 1600);
}

/**
 * DOM adapter — for anything whose anchors are real elements in the page:
 * docx/pdf/pptx <mark>s, and the screenshot/image rectangles and pins.
 *
 * `signature` re-runs the observer wiring when the marker set changes (new
 * comment, tab switch, state switch, visibility filter). Pass something that
 * changes exactly then.
 */
export function useDomCommentAnchors(
  /**
   * The content element itself, NOT a ref.
   *
   * A ref cannot tell an effect when its node arrives, and here the node
   * arrives LATE: DocumentViewer returns a "parsing…" placeholder until the
   * parsed content lands, so on first render there is no `.viewer-content` at
   * all. With a ref the effect ran once against null, bailed, and never
   * re-ran — the signature has no reason to change when sections appear — so
   * the spy was dead on every file cycle while the jump kept working, because
   * the jump queries the DOM on demand. Callers pass a callback ref (see
   * DocumentViewer.attachContentRef), which makes attachment a state change
   * and re-runs this.
   */
  root: HTMLElement | null,
  signature: string
): CommentAnchorApi {
  const [visible, setVisible] = useState<string[]>([]);
  const [ordered, setOrdered] = useState<string[]>([]);
  const [hovered, setHoveredId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  /**
   * Content -> rail hover, by DELEGATION on the content root.
   *
   * One listener rather than props on each marker renderer, for the reason
   * the MutationObserver below exists: there are four marker renderers across
   * two viewers and they have already drifted from each other once. A
   * delegated listener covers every marker that exists now and every one
   * added later, including markers drawn by a path nobody remembers to
   * update.
   *
   * `mouseover`/`mouseout` rather than `mouseenter`/`mouseleave` because only
   * the former bubble, and delegation needs bubbling. The relatedTarget check
   * is what makes them behave like enter/leave: moving between two children
   * of the same marker fires out-then-over, and without it the highlight
   * flickers off and on across a <mark> that spans a line break.
   */
  useEffect(() => {
    if (!root) { setHoveredId(null); return; }
    const idOf = (t: EventTarget | null): string | null => {
      const el = t instanceof Element ? t.closest('[data-comment-id], [data-comment-ids]') : null;
      return el ? (idsOnElement(el)[0] || null) : null;
    };
    const onOver = (e: Event) => setHoveredId(idOf(e.target));
    const onOut = (e: Event) => {
      const to = (e as MouseEvent).relatedTarget;
      if (to instanceof Node && root.contains(to) && idOf(to)) return;
      setHoveredId(idOf(to));
    };
    root.addEventListener('mouseover', onOver);
    root.addEventListener('mouseout', onOut);
    return () => {
      root.removeEventListener('mouseover', onOver);
      root.removeEventListener('mouseout', onOut);
    };
  }, [root]);

  useEffect(() => {
    if (!root || typeof IntersectionObserver === 'undefined') {
      setVisible([]);
      setOrdered([]);
      return;
    }

    const ratios = new Map<Element, number>();
    let observed: Element[] = [];
    let frame = 0;

    const idsOf = (els: Element[], onlyOnScreen: boolean): string[] => {
      const out: string[] = [];
      // `observed` comes from querySelectorAll, which is document order — so
      // the highlight follows the reading order rather than the order the
      // observer happened to fire in, and the rail's list follows the page.
      for (const el of els) {
        if (onlyOnScreen && (ratios.get(el) || 0) <= 0) continue;
        for (const id of idsOnElement(el)) {
          if (!out.includes(id)) out.push(id);
        }
      }
      return out;
    };

    const sameList = (a: string[], b: string[]) =>
      a.length === b.length && a.every((v, i) => v === b[i]);

    const recompute = () => {
      const onScreen = idsOf(observed, true);
      setVisible(prev => sameList(prev, onScreen) ? prev : onScreen);
    };

    const io = new IntersectionObserver(entries => {
      for (const e of entries) ratios.set(e.target, e.isIntersecting ? e.intersectionRatio || 1 : 0);
      recompute();
    }, {
      /**
       * The READING BAND, not the viewport (2026-08-26).
       *
       * -12%/-12% still meant "almost anywhere on screen", so on a page with
       * several comments the highlight walked the whole list as you scrolled
       * and told the reader nothing. These margins shrink the observer's root
       * to the band between READING_BAND.top and .bottom of the viewport —
       * where someone actually reads — so at most the comment under your eye
       * is current.
       */
      rootMargin: `${-READING_BAND.top * 100}% 0px ${-(1 - READING_BAND.bottom) * 100}% 0px`,
      threshold: [0, 0.25]
    });

    const rescan = () => {
      const found = Array.from(root.querySelectorAll('[data-comment-id], [data-comment-ids]'));
      const same = found.length === observed.length && found.every((el, i) => el === observed[i]);
      if (same) return;
      io.disconnect();
      ratios.clear();
      observed = found;
      for (const el of observed) io.observe(el);
      // Marker positions only change when the marker SET changes, so the
      // order is computed here rather than on every intersection callback.
      const inOrder = idsOf(observed, false);
      setOrdered(prev => sameList(prev, inOrder) ? prev : inOrder);
      recompute();
    };

    rescan();

    /**
     * WHY A MUTATION OBSERVER AND NOT A DEPENDENCY LIST
     *
     * The first version wired the IntersectionObserver once per `signature`
     * and that was wrong in the ordinary case, not an exotic one: the markers
     * are drawn from PARSED CONTENT, which arrives from its own fetch after
     * mount. The effect ran against an empty document, observed nothing, and
     * nothing in the signature — active tab, state id, comment ids — changes
     * when the sections finally render. So the spy was permanently dead on
     * every file cycle, while the jump (which queries on demand) worked
     * perfectly, and the difference between them is invisible without
     * measuring: no error, no warning, just a highlight that never appears.
     *
     * Driving it off the DOM itself means every future path that renders a
     * marker — a new format, a lazily loaded image, a state switch — is
     * covered without anyone remembering to add it to a list. The observer is
     * scoped to the content root, which does not contain the rail, so the
     * re-render this causes cannot feed back into it.
     */
    const mo = typeof MutationObserver !== 'undefined'
      ? new MutationObserver(() => {
          if (frame) return;
          frame = window.requestAnimationFrame(() => { frame = 0; rescan(); });
        })
      : null;
    mo?.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-comment-id', 'data-comment-ids']
    });

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      mo?.disconnect();
      io.disconnect();
    };
  }, [root, signature]);

  /**
   * THE PASSAGE LANDS WHERE THE READER IS LOOKING, NOT IN THE MIDDLE OF THE
   * SCREEN (2026-08-27, Fredrik: "the highlighted comment in the actual
   * content appears in the middle of the viewport, not next to the comment").
   *
   * `block: 'center'` put it at 50%, while the rail put the open row at the
   * top of its own column — so the two halves of one thing ended up at
   * different heights and read as unrelated. Landing it at the top of the
   * READING BAND makes the rail's alignment possible AND settles a second
   * disagreement for free: a passage dropped at 50% is below the band, so the
   * spy did not consider the comment you just jumped to to be the one you
   * were reading.
   *
   * scroll-margin rather than arithmetic on window.scrollY, because
   * scrollIntoView resolves whichever scroller actually holds the content and
   * a hand-computed window scroll assumes it is the page. Set inline so the
   * fraction has exactly one home — this file — instead of being restated as
   * a number in the stylesheet that nothing keeps in step.
   */
  const reveal = useCallback((commentId: string): boolean => {
    if (!root) return false;
    const el = root.querySelector(selectorFor(commentId));
    if (!el) return false;
    (el as HTMLElement).style.scrollMarginTop = `${Math.round(READING_BAND.top * 100)}vh`;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    flashAnchor(el);
    return true;
  }, [root]);

  /**
   * STATE, THEN AN EFFECT — not a class written straight onto the element
   * (2026-08-27, Fredrik: the rail-hover highlight "seems to work once").
   *
   * Writing the class imperatively means it is applied exactly once, at the
   * moment of the call, onto whichever element existed then. Nothing puts it
   * back. Any render that replaces a marker — a new comment, a tab switch, a
   * visibility filter, a re-parse — silently drops it, and the highlight is
   * gone with no error and no way to tell from the outside that it was ever
   * meant to be there.
   *
   * Held as state and re-applied by an effect, the class is a FUNCTION of
   * what is hovered and what markers exist (`signature` changes exactly when
   * the marker set does). It cannot drift out of sync with either.
   *
   * It also makes the hover symmetric, which is the half Fredrik could see:
   * `hoveredCommentId` is now set from BOTH ends, so hovering a rail row
   * lights the row AND the passage, instead of lighting the passage and
   * leaving the row the pointer is actually on unmarked.
   */
  const setActive = useCallback((commentId: string | null): void => {
    setActiveId(commentId);
  }, []);

  const setHovered = useCallback((commentId: string | null): void => {
    setHoveredId(commentId);
  }, []);

  useEffect(() => { applyActiveAnchor(root, activeId); }, [root, activeId, signature]);
  useEffect(() => { applyHoverAnchor(root, hovered); }, [root, hovered, signature]);

  const anchorRect = useCallback((commentId: string): DOMRect | null => {
    if (!root) return null;
    const el = root.querySelector(selectorFor(commentId));
    return el ? el.getBoundingClientRect() : null;
  }, [root]);

  const hasAnchor = useCallback((commentId: string): boolean => {
    return !!root && !!root.querySelector(selectorFor(commentId));
    // `signature` is unread inside, and deliberately in the deps: a comment
    // gains or loses its marker when the tab, the web-page state or the
    // comment set changes, and an identity that never changed would leave the
    // rail holding a stale "no anchor" — the jump would be hidden for good on
    // a comment that has one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root, signature]);

  return useMemo(
    () => ({
      reveal, setActive, visibleCommentIds: visible, orderedCommentIds: ordered,
      hasAnchor, setHovered, hoveredCommentId: hovered, anchorRect
    }),
    [reveal, setActive, visible, ordered, hasAnchor, setHovered, hovered, anchorRect]
  );
}
